import { FOUNDER_CALL_LABEL, FOUNDER_CALL_URL } from '../founderCall';
import type { FeatureContent } from './types';

export const aiCandidateProfiles: FeatureContent = {
  slug: 'ai-candidate-profiles',
  path: '/features/ai-candidate-profiles',
  scorecardConnector:
    'The profile layer of the candidate scorecard: the rich, matchable record every other layer is built on.',
  audience: 'company',
  icon: 'FileText',
  name: 'Profile Generator',
  stage: 2,
  navLabel: 'AI Candidate Profiles',
  navDescription: 'Turn any CV, in any language, into a rich, matchable profile in seconds.',
  primaryKeyword: 'CV parsing',
  seo: {
    title: 'AI CV Parsing & Resume Parser | Talenture.ai',
    description:
      'AI CV parsing that turns any resume, in 100+ languages, into a rich, matchable profile in seconds, with skills, seniority, and quantified wins extracted.',
    keywords: [
      'CV parsing',
      'resume parser',
      'AI CV parsing',
      'multilingual CV parsing',
      'resume parsing software',
      'AI candidate profile',
      'skill progression',
    ],
    ogTitle: 'AI CV Parsing in 100+ Languages | Talenture.ai',
    ogDescription:
      'Every CV, every language, instantly structured into a rich profile. Skills with proficiency and recency, skill progression, seniority, education, and quantified wins, extracted in seconds.',
  },
  hero: {
    eyebrow: 'Stage 2 of the hiring mission · Find and understand candidates',
    whereThisFits:
      'Every CV becomes a structured profile here, so matching has real data to score, not keywords.',
    h1: 'Every CV is a different shape. Reading 200 of them is a week you don\'t have.',
    subhead:
      'Talenture turns any CV, in 100+ languages, into a rich, comparable profile in seconds: skills with recency, dated history, and 4–8 quantified achievements. Ready to rank the moment it lands.',
    worksWithAts: true,
    primaryCta: { label: 'See a sample scorecard', href: '/sample-scorecard' },
    secondaryCta: { label: FOUNDER_CALL_LABEL, href: FOUNDER_CALL_URL },
  },
  definition: {
    heading: 'What is AI CV parsing?',
    body: 'AI CV parsing reads an unstructured resume and extracts the facts a recruiter needs: skills with proficiency, seniority, education, dated work history, and quantified wins, organised into consistent fields. Talenture does this in over 100 languages and enriches every profile so it is ready to match and compare instantly.',
  },
  sections: [
    {
      icon: 'Globe',
      title: 'Multilingual by design',
      body: 'A CV in Dutch, Spanish, or Mandarin becomes a profile you can compare like any other. Proper nouns stay in the original language, while roles and skills are translated to English for uniform matching.',
      bullets: [
        'CV parsing in 100+ languages',
        'Names and companies preserved accurately',
        'Skills normalised for fair comparison',
      ],
    },
    {
      icon: 'TrendingUp',
      title: 'Skill progression, not a flat list',
      body: 'A list of skills tells you what someone has touched. Talenture reads how those skills developed across the candidate\'s dated roles, so you see depth and direction, not just a tag cloud.',
      bullets: [
        'Proficiency estimated per skill, not a yes or no',
        'Skills clustered into domains to show real expertise',
        'Career trajectory read from dated, ordered roles',
        'Title progression captured, for example developer to lead',
      ],
    },
    {
      icon: 'Award',
      title: 'Achievements, not just job titles',
      body: 'Talenture pulls out quantifiable wins so you see impact at a glance: percentages, currency figures, and concrete outcomes drawn straight from the candidate\'s own words.',
      bullets: [
        '4 to 8 quantified achievements per profile',
        'Hard skills, soft skills, and tooling separated',
        'Action verbs and measurable results highlighted',
      ],
    },
    {
      icon: 'Clock',
      title: 'Skills weighted by recency',
      body: 'A skill used last year counts for more than one last touched a decade ago. Every skill is weighted by when it was last used, so current strengths stand out and stale ones fade.',
    },
    {
      icon: 'Layers',
      title: 'A profile rich enough to match on',
      body: 'By the time parsing finishes, a profile is far more than text. It carries the structured signals the matching engine needs to rank fairly across every dimension.',
      bullets: [
        'Location geocoded to real coordinates for commute scoring',
        'Seniority inferred from titles when a CV does not state it',
        'Languages, workplace values, and salary expectations captured',
        'A vector embedding built for semantic matching',
      ],
    },
  ],
  visual: {
    component: 'CandidateProfileScreen',
    url: 'app.talenture.ai/candidates/mira-n/profile',
    caption: 'One CV, read once and turned into a profile you can compare against every other.',
  },
  supportingVisuals: [
    {
      component: 'CandidatePreferencesScreen',
      url: 'app.talenture.ai/candidates/mira-n/preferences',
      heading: 'What the CV never says',
      caption:
        'Salary, notice period and work preferences, collected up front: the deal-breakers that otherwise surface at the interview.',
    },
    {
      component: 'MatchScorecardScreen',
      url: 'app.talenture.ai/candidates/mira-n/match',
      heading: 'The profile at work',
      caption:
        'The same profile scored against one role. The rail walks the whole record: match, evidence, notes, practice interview, recap.',
    },
  ],
  underTheHood: {
    intro:
      'How a PDF becomes a structured profile in seconds, for anyone who wants to know what is actually happening to the file.',
    sections: [
      {
        icon: 'Cpu',
        title: 'Parallel extraction passes',
        body: 'Rather than reading a CV once from top to bottom, Talenture runs several extraction passes at the same time, each responsible for a different part of the record. That is why a complete profile takes seconds rather than minutes, and why the fields come out consistent across every candidate.',
        bullets: [
          'Personal and contact details',
          'Seniority and career level',
          'Education, degrees, and certifications',
          'Detailed, dated job-experience history',
        ],
      },
      {
        icon: 'Clock',
        title: 'The half-life recency model',
        body: 'Skill recency is not a cut-off date. Each skill decays on a half-life curve from the last dated role it appears in, so a skill used two years ago still counts for something while one last touched a decade ago fades close to zero. This is what stops an outdated stack from ranking like a current one.',
      },
    ],
  },
  howTo: {
    heading: 'How AI candidate profiles work',
    intro: 'Three steps from an unstructured CV to a rich, matchable profile.',
    steps: [
      {
        title: 'Upload a CV in any language',
        body: 'Talenture reads the candidate CV in over 100 languages, preserving proper nouns while translating roles and skills to English for consistent comparison.',
      },
      {
        title: 'Extract the signals that matter',
        body: 'Parallel extraction passes identify personal details, seniority, education, dated work history, skills, and quantified achievements.',
      },
      {
        title: 'Enrich the profile for matching',
        body: 'Skills receive proficiency and recency context, while location, seniority, preferences, and a semantic embedding make the profile ready to rank.',
      },
    ],
  },
  proofPoints: [
    { value: '100+', label: 'languages parsed' },
    { value: '4 to 8', label: 'quantified wins per profile' },
    { value: 'Seconds', label: 'from upload to matchable profile' },
  ],
  faq: [
    {
      question: 'Which file formats and languages are supported?',
      answer:
        'Talenture parses standard CV formats and handles over 100 languages. Proper nouns stay in the original language while roles and skills are translated to English, so a multilingual pipeline can still be compared on equal terms.',
    },
    {
      question: 'Does it show skill progression, not just a list?',
      answer:
        'Yes. Talenture estimates proficiency per skill, clusters skills into domains to show genuine expertise, and reads career trajectory and title progression from the candidate\'s dated work history. You see depth and direction, not a flat list.',
    },
    {
      question: 'What signals end up in a parsed profile?',
      answer:
        'Each profile carries skills split into hard skills, soft skills, and tooling, with proficiency and recency, plus seniority, geocoded location, languages, workplace values, salary expectations, education, quantified achievements, and a vector embedding for semantic matching.',
    },
    {
      question: 'How accurate is the extraction?',
      answer:
        'Each profile is built from several parallel extraction passes covering personal details, seniority, education, and dated work history. Skills are categorised into hard skills, soft skills, and tooling, and quantified achievements are pulled directly from the CV text.',
    },
    {
      question: 'Do I still need to screen CVs manually?',
      answer:
        'No. Once a CV is parsed, the profile flows straight into matching and ranking. You review structured profiles and scores instead of reading raw documents, which removes the data-entry and first-pass screening work.',
    },
    {
      question: 'What happens to skills the candidate listed years ago?',
      answer:
        'They still count, but less. A half-life recency weighting reduces the score of skills that have not been used recently, so a profile reflects what the candidate can do now, not only what they once did.',
    },
    {
      question: 'Is candidate data handled securely?',
      answer:
        'Yes. All candidate data is GDPR-compliant, candidates can request deletion at any time, and nothing is shared with third parties. Parsing exists to structure data for matching, not to retain it beyond what the workflow needs.',
    },
  ],
  relatedSlugs: ['ai-candidate-matching', 'ai-job-description-generator', 'match-insights'],
};
